/**
 * API 基础配置 - 性能优化版
 * 优化点：请求缓存、请求取消、统一错误处理
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { invalidateCsrfToken, setupCsrfInterceptor } from '../utils/csrfInterceptor';
import { message } from '../utils/message';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** 关闭/开启 GET 请求级别的本地缓存（默认开启；下载/blob/写操作建议关闭） */
    enableCache?: boolean;
    /** 为 true 时不附加 CSRF（登录/登出等后端已排除接口） */
    skipCsrf?: boolean;
  }
  interface InternalAxiosRequestConfig {
    enableCache?: boolean;
    skipCsrf?: boolean;
  }
}

type InternalAxiosRequestConfigWithCache = InternalAxiosRequestConfig;
import { triggerLogout } from '../utils/authEventManager';
import { usePermissionStore } from '../store/permissionStore';

const CONFIG = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL || '/api',
  TIMEOUT: 30000,
  TOKEN_KEY: 'token',
  USER_KEY: 'user',
  REQUEST_CACHE_SIZE: 50,
  REQUEST_CACHE_TIME: 5000,
};

interface CacheEntry {
  data: unknown;
  timestamp: number;
}

const requestCache = new Map<string, CacheEntry>();

function getCacheKey(config: AxiosRequestConfig | undefined): string {
  if (!config) {
    return 'unknown:unknown:{}:{}';
  }
  return `${config.method ?? 'get'}:${config.url ?? ''}:${JSON.stringify(config.params || {})}:${JSON.stringify(config.data || {})}`;
}

function getFromCache(key: string): unknown | null {
  const cached = requestCache.get(key);
  if (cached && Date.now() - cached.timestamp < CONFIG.REQUEST_CACHE_TIME) {
    return cached.data;
  }
  requestCache.delete(key);
  return null;
}

function setToCache(key: string, data: unknown): void {
  if (requestCache.size >= CONFIG.REQUEST_CACHE_SIZE) {
    const firstKey = requestCache.keys().next().value as string | undefined;
    if (firstKey !== undefined) {
      requestCache.delete(firstKey);
    }
  }
  requestCache.set(key, { data, timestamp: Date.now() });
}

type InternalAxiosRequestConfigWithPending = InternalAxiosRequestConfigWithCache & {
  __requestKey?: string;
  __requestController?: AbortController;
};

const pendingRequests = new Map<string, Set<AbortController>>();

function addPendingRequest(config: InternalAxiosRequestConfigWithPending): void {
  const key = getCacheKey(config);
  const controller = new AbortController();
  config.signal = controller.signal;
  config.__requestKey = key;
  config.__requestController = controller;
  const bucket = pendingRequests.get(key);
  if (bucket) {
    bucket.add(controller);
    return;
  }
  pendingRequests.set(key, new Set([controller]));
}

function removePendingRequest(config: AxiosRequestConfig | undefined): void {
  if (!config) {
    return;
  }
  const internal = config as InternalAxiosRequestConfigWithPending;
  const key = internal.__requestKey || getCacheKey(config);
  const controller = internal.__requestController;
  const bucket = pendingRequests.get(key);
  if (!bucket) {
    return;
  }
  if (controller) {
    bucket.delete(controller);
  }
  if (bucket.size === 0) {
    pendingRequests.delete(key);
  }
}

export function cancelPendingRequests(): void {
  pendingRequests.forEach((controllers) => {
    controllers.forEach((controller) => controller.abort());
  });
  pendingRequests.clear();
}

const api: AxiosInstance = axios.create({
  baseURL: CONFIG.BASE_URL,
  timeout: CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 注册 CSRF（必须在默认 axios 实例创建且挂载鉴权请求拦截器之后、统一响应解包之前）
setupCsrfInterceptor(api);

let hasShown401Modal = false;

function requestHadAuthToken(cfg?: InternalAxiosRequestConfig): boolean {
  const hdrs = cfg?.headers;
  if (!hdrs) {
    return false;
  }
  const get = (key: string): string | undefined => {
    const h = hdrs as Record<string, unknown> & { get?: (k: string) => unknown };
    if (typeof h.get === 'function') {
      const v = h.get(key);
      return typeof v === 'string' ? v : undefined;
    }
    const rec = hdrs as Record<string, string | undefined>;
    return rec[key] ?? rec[key.toLowerCase()];
  };
  const auth = get('Authorization') ?? get('authorization');
  const st = get('satoken');
  return Boolean((auth && auth.length > 0) || (st && st.length > 0));
}

const AUTH_ANON_PATHS = ['/auth/login', '/auth/logout'];

function isAnonymousAuthRequest(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_ANON_PATHS.some((p) => url.includes(p));
}

api.interceptors.request.use(
  (config) => {
    if (!config) {
      return Promise.reject(new Error('axios 请求配置缺失'));
    }
    const cfg = config as InternalAxiosRequestConfigWithPending;
    const token = localStorage.getItem(CONFIG.TOKEN_KEY);
    if (token && !isAnonymousAuthRequest(cfg.url)) {
      // 生产 Sa-Token token-prefix 为空时，Bearer 会导致整段被当作 token 而 401；优先 satoken
      cfg.headers['satoken'] = token;
      delete (cfg.headers as Record<string, unknown>)['Authorization'];
      delete (cfg.headers as Record<string, unknown>)['authorization'];
    }

    // FormData 须由浏览器自动带 multipart boundary，不能沿用默认 application/json
    if (typeof FormData !== 'undefined' && cfg.data instanceof FormData) {
      const hdrs = cfg.headers as Record<string, unknown> & {
        set?: (k: string, v: string) => void;
        delete?: (k: string) => void;
      };
      if (typeof hdrs.set === 'function' && typeof hdrs.delete === 'function') {
        hdrs.delete('Content-Type');
        hdrs.delete('content-type');
      } else {
        delete hdrs['Content-Type'];
        delete hdrs['content-type'];
      }
    }

    addPendingRequest(cfg);

    if (cfg.method === 'get' && cfg.enableCache !== false) {
      const cacheKey = getCacheKey(cfg);
      const cachedData = getFromCache(cacheKey);
      if (cachedData !== null) {
        const adapter = () => Promise.resolve({
          data: cachedData,
          status: 200,
          statusText: 'OK',
          headers: {},
          config: cfg,
          request: {},
        });
        cfg.adapter = adapter;
      }
    }

    return cfg;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    removePendingRequest(response.config);

    const outCfg = response.config as InternalAxiosRequestConfigWithPending | undefined;
    if (outCfg && outCfg.method === 'get' && outCfg.enableCache !== false) {
      const cacheKey = getCacheKey(outCfg);
      setToCache(cacheKey, response.data);
    }
    return response.data;
  },
  (error) => {
    if (error.config) {
      removePendingRequest(error.config);
    }

    if (axios.isCancel(error)) {
      return Promise.reject({ __CANCELLED__: true });
    }

    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401: {
          // 未携带 token 的 401（竞态/首帧）不应清空本地登录态，否则 Strict Mode 下会误登出
          if (!requestHadAuthToken(error.config)) {
            break;
          }

          localStorage.removeItem(CONFIG.TOKEN_KEY);
          localStorage.removeItem(CONFIG.USER_KEY);
          localStorage.removeItem('nexus_crm_auth');
          invalidateCsrfToken();

          usePermissionStore.getState().clearPermissions();

          if (!window.location.pathname.startsWith('/login')) {
            const currentPath = window.location.pathname + window.location.search;
            sessionStorage.setItem('redirect_after_login', currentPath);

            if (!hasShown401Modal) {
              hasShown401Modal = true;
              message.warning(
                '登录已失效（可能已过期或在其他窗口重新登录）。正在跳转到登录页…',
              );
              // 立即跳转，避免留在页面上连续触发 401/误操作
              setTimeout(() => {
                hasShown401Modal = false;
                triggerLogout();
              }, 300);
            }
          }
          break;
        }
        case 403:
          message.error('没有权限访问此资源');
          break;
        case 500:
          message.error('服务器内部错误，请稍后重试');
          break;
        default:
          {
            const errMsg = data?.message || `请求失败(${status})`;
            message.error(errMsg);
          }
      }
    } else if (error.request) {
      message.error('网络错误，请检查网络连接');
    }
    return Promise.reject(error);
  }
);

export function cleanup(): void {
  cancelPendingRequests();
  requestCache.clear();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', cleanup);
}

export default api;
